import React, { Component } from "react";
import {
  Form,
  Input,
  Button,
  Modal,
  Empty,
  Card,
  Tabs,
  Space,
  Select,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Image as ImageIcon, Globe, Radio, ExternalLink } from "lucide-react";
import MainLayout from "../../components/layouts/main";
import Constants from "../../data/constants";
import LabATM from "../../components/LabATM";

// import './MediaPage.css'
import "antd/dist/antd.css";
import StyledDropzone from "../../components/StyledDropzone";
import MediaListElement from "../../components/MediaListElement";
import MediaPreviewPopup from "../../components/popups/MediaPreviewPopup";
import { connect } from "react-redux";
import {
  requestFilesServerPost,
  requestServerGet,
  requestServerPost,
  selectBrand,
  selectPage,
} from "../../actions";
import path from "path";
import DataHelper from "../../helpers/DataHelper";
import PopupManager from "../../managers/PopupManager";
import CreateURLMediaView from "../views/CreateURLMediaView";
import CreateStreamingURLMediaView from "../views/CreateStreamingURLMediaView";
import CreateDirectoryURLMediaView from "../views/CreateDirectoryURLMediaView";
import useFolderFileStore from "../../store/folderFileStore";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const { Search } = Input;
const { confirm } = Modal;
const { TabPane } = Tabs;

class FilesUploader extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      images: [],
      mediaDatas: [],
      uploadProgress: 0,
      branches: [],
      imageVideoAccessBranches: ["all"],
      collapsed: false,
      isMobile: typeof window !== "undefined" && window.innerWidth < 640,
    };
    const { onToggleSideMenu } = props;

    this.clearFiles = () => {};
    this.uploadProgressUpdate = () => {};
    this.toggle = onToggleSideMenu;
    this.mediaPreviewPopupRef = React.createRef();
  }

  handleResize = () => {
    this.setState({ isMobile: window.innerWidth < 640 });
  };

  componentDidMount() {
    if (this.props.selectedBrandData) {
      this.updateMediaListData();
      this.loadBranches();
    }
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  componentDidUpdate = (newProps) => {
    if (
      "selectedBrandData" in newProps &&
      this.props.selectedBrandData !== newProps.selectedBrandData
    ) {
      this.updateMediaListData();
    }
  };

  loadBranches = () => {
    const { selectedBrandData } = this.props;
    if (
      selectedBrandData &&
      (selectedBrandData.ContentSharingMode === 2 ||
        selectedBrandData.ContentSharingMode === "2")
    ) {
      DataHelper.getBranches(
        { brandid: selectedBrandData.BrandID },
        this.props,
      ).then((data) => {
        this.setState({ branches: data || [] }, () => {
          // After branches load, pre-select the folder's allowed branches as the default
          this.setState({
            imageVideoAccessBranches: this.getDefaultAccessBranches(),
          });
        });
      });
    }
  };

  handleImageVideoAccessBranchesChange = (value) => {
    let newVal;
    const current = this.state.imageVideoAccessBranches;
    if (value.includes("all") && !current.includes("all")) {
      newVal = ["all"];
    } else if (current.includes("all") && value.length > 1) {
      newVal = value.filter((v) => v !== "all");
    } else if (value.length === 0) {
      newVal = ["all"];
    } else {
      newVal = value;
    }
    this.setState({ imageVideoAccessBranches: newVal });
  };

  updateMediaListData = () => {
    const { selectedBrandData } = this.props;

    var params = { brandid: selectedBrandData.BrandID };

    DataHelper.getFiles(params, this.props).then((data) => {
      this.setState({ mediaDatas: data });
    });
  };

  onProgress = (percentCompleted) => {
    this.setState({ uploadProgress: percentCompleted });
  };

  handleFilesUpload = (files) => {
    const { selectedBrandData, selectedBranchData } = this.props;
    const isLockedToBranch =
      selectedBrandData?.ContentSharingMode == 2 && !!selectedBranchData;
    const { imageVideoAccessBranches } = this.state;
    let accessbranchesValue;
    if (isLockedToBranch) {
      // Branch level: DataHelper locks to the active branch automatically
      accessbranchesValue = null;
    } else if (selectedBrandData?.ContentSharingMode == 2) {
      // Brand level mode 2: clamp selection to the folder's allowed branches
      const effective = this.getEffectiveBranches();
      const effectiveIds = new Set(effective.map((b) => String(b.BranchID)));
      const clamped = imageVideoAccessBranches.filter(
        (v) => v === "all" || effectiveIds.has(v),
      );
      const finalVal =
        clamped.length === 0 || clamped.includes("all")
          ? "all"
          : clamped.join(",");
      // If folder is restricted and user chose "all", use the folder's branch IDs instead
      accessbranchesValue =
        finalVal === "all" && effective.length < this.state.branches.length
          ? effective.map((b) => String(b.BranchID)).join(",")
          : finalVal;
    } else {
      accessbranchesValue = imageVideoAccessBranches.includes("all")
        ? "all"
        : imageVideoAccessBranches.join(",");
    }
    DataHelper.handleFilesUpload(
      files,
      this.props,
      this.uploadProgressUpdate,
      this.onProgress,
      this.state.uploadProgress,
      accessbranchesValue,
    ).then(() => {
      this.updateMediaListData();
    });
  };

  onDrop = (files) => {
    this.setState({ images: files });
  };

  mapClearFileFunction = (func) => {
    this.clearFiles = func;
  };

  mapUploadProgressUpdate = (func) => {
    this.uploadProgressUpdate = func;
  };

  deleteMedia = (data, callback = () => {}) => {
    const { usertoken, requestServerPost } = this.props;
    const t = (PRO_TRANSLATIONS[this.props.lang] || PRO_TRANSLATIONS.en)
      .filesUploader;

    const params = { fileid: data.FileID };

    confirm({
      title: t.deleteConfirm,
      icon: <ExclamationCircleOutlined />,
      onOk() {
        requestServerPost(
          "delete_file",
          params,
          {},
          (response) => {
            callback();
          },
          usertoken,
        );
      },
      onCancel() {},
    });
  };

  previewMedia = (data) => {
    if (data && this.mediaPreviewPopupRef.current) {
      this.mediaPreviewPopupRef.current.previewMedia(data);
    }
  };

  // Returns branch list filtered to those allowed by the currently selected folder's AccessBranches.
  // Walks the full ancestor chain so a child "all" folder inherits the parent's restriction.
  getEffectiveBranches = () => {
    const { branches } = this.state;
    const { selectedBrandData, selectedBranchData } = this.props;
    if (!(selectedBrandData?.ContentSharingMode == 2) || selectedBranchData)
      return branches;
    const { folderFileDatas, selectedFolderID } = useFolderFileStore.getState();
    if (!selectedFolderID) return branches;

    // Build ancestor chain from selected folder up to root
    const chain = [];
    let currentId = String(selectedFolderID);
    const visited = new Set();
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const folder = folderFileDatas.find(
        (f) => String(f.FolderID) === currentId,
      );
      if (!folder) break;
      chain.push(folder);
      currentId =
        folder.FolderParentID != null ? String(folder.FolderParentID) : null;
    }

    // Start with all branches; intersect down with each ancestor that has an explicit restriction
    let effectiveIds = null; // null = unrestricted (all)
    for (const folder of chain) {
      const raw = folder.AccessBranches;
      if (!raw || raw === "null" || raw === "all" || raw === "") continue; // unrestricted — skip
      const folderIds = new Set(
        String(raw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      );
      if (effectiveIds === null) {
        effectiveIds = folderIds;
      } else {
        // Intersect: keep only IDs present in both
        effectiveIds = new Set(
          [...effectiveIds].filter((id) => folderIds.has(id)),
        );
      }
    }

    if (effectiveIds === null) return branches; // no restriction in entire chain
    return branches.filter((b) => effectiveIds.has(String(b.BranchID)));
  };

  // Returns the default accessBranches selection for a new media item.
  // "all" when folder is unrestricted; specific branch IDs when folder is restricted.
  getDefaultAccessBranches = () => {
    const effective = this.getEffectiveBranches();
    if (effective.length === this.state.branches.length) return ["all"];
    return effective.map((b) => String(b.BranchID));
  };

  renderBranchSelector = (label) => {
    const { imageVideoAccessBranches } = this.state;
    const { selectedBrandData, selectedBranchData } = this.props;
    const t = (PRO_TRANSLATIONS[this.props.lang] || PRO_TRANSLATIONS.en)
      .filesUploader;
    // At branch level in mode 2, the branch is fixed — no need to show the selector
    if (selectedBrandData?.ContentSharingMode == 2 && !!selectedBranchData)
      return null;
    const effectiveBranches = this.getEffectiveBranches();
    if (!effectiveBranches || effectiveBranches.length === 0) return null;
    return (
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: 8,
          justifyContent: "flex-start",
          alignItems: "center",
          width: "100%",
        }}
      >
        <label style={{ fontWeight: "500", color: "#666", marginBottom: 4 }}>
          {label || t.accessibleBranches}
        </label>
        <Select
          allowClear
          mode="multiple"
          style={{
            minWidth: 200,
            flex: 1,
            border: "1.5px solid #ccc",
            borderRadius: "5px",
            overflow: "hidden",
          }}
          value={imageVideoAccessBranches}
          onChange={this.handleImageVideoAccessBranchesChange}
          maxTagCount="responsive"
        >
          {effectiveBranches.length === this.state.branches.length && (
            <Select.Option key="all" value="all">
              {t.allBranches}
            </Select.Option>
          )}
          {effectiveBranches.map((b) => (
            <Select.Option key={b.BranchID} value={String(b.BranchID)}>
              {b.BranchName}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  };

  render = () => {
    const { branches, isMobile } = this.state;
    const { selectedBrandData, selectedBranchData } = this.props;
    const t = (PRO_TRANSLATIONS[this.props.lang] || PRO_TRANSLATIONS.en)
      .filesUploader;
    const isLockedToBranch =
      selectedBrandData?.ContentSharingMode == 2 && !!selectedBranchData;
    // At branch level in mode 2 pass an empty array to hide the branch selector in child views
    // At brand level mode 2 inside a folder, restrict to the folder's allowed branches
    const childBranches = isLockedToBranch ? [] : this.getEffectiveBranches();
    // Show "All Branches" only when the list is unrestricted (all brand branches are available)
    const allBranchesOption = childBranches.length === branches.length;
    // Default selection for URL sub-views: specific branch IDs when folder-restricted, else "all"
    const initialAccessBranches = this.getDefaultAccessBranches();
    const tabLabel = (icon, label) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        {icon}
        <span>{label}</span>
      </span>
    );

    const sectionHeader = (label) => (
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 700,
          color: "#6366f1",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          background: "#eef2ff",
          borderRadius: 6,
          padding: "3px 10px",
          marginBottom: 14,
        }}
      >
        {label}
      </div>
    );

    return (
      <div
        style={{
          width: "100%",
          minHeight: "450px",
          padding: isMobile ? "16px" : "24px",
          boxSizing: "border-box",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: isMobile ? 16 : 24 }}>
          <h2
            style={{
              fontSize: isMobile ? 18 : 22,
              fontWeight: 700,
              color: "#0f172a",
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            {t.title}
          </h2>
          <p style={{ fontSize: 14, color: "#64748b", margin: "4px 0 0 0" }}>
            {t.subtitle}
          </p>
        </div>
        <Tabs tabPosition={isMobile ? "top" : "left"}>
          <TabPane
            tab={tabLabel(<ImageIcon size={15} />, t.tabImageVideo)}
            key="1"
          >
            <div style={{ padding: isMobile ? "12px 0 8px" : "16px 12px 8px" }}>
              {this.renderBranchSelector(t.accessibleBranches)}
              <StyledDropzone
                handleFilesUpload={this.handleFilesUpload}
                clearFilesMapper={this.mapClearFileFunction}
                updateUploadProgressMapper={this.mapUploadProgressUpdate}
                onFinishUpload={() => {
                  PopupManager.closePopup();
                }}
              />
            </div>
          </TabPane>
          <TabPane tab={tabLabel(<Globe size={15} />, t.tabWebsite)} key="2">
            <div style={{ padding: isMobile ? "12px 0 8px" : "16px 12px 8px" }}>
              <Tabs
                items={[
                  {
                    label: t.websiteUrl,
                    key: "website",
                    children: (
                      <div style={{ paddingTop: 8 }}>
                        {sectionHeader(t.createWebUrl)}
                        <CreateURLMediaView
                          branches={childBranches}
                          allBranchesOption={allBranchesOption}
                          initialAccessBranches={initialAccessBranches}
                          onFinish={() => this.showUploadPage(false)}
                        />
                      </div>
                    ),
                  },
                  {
                    label: t.directoryUrl,
                    key: "directory",
                    children: (
                      <div style={{ paddingTop: 8 }}>
                        {sectionHeader(t.createDirectoryUrl)}
                        <CreateDirectoryURLMediaView
                          branches={childBranches}
                          allBranchesOption={allBranchesOption}
                          initialAccessBranches={initialAccessBranches}
                          onFinish={() => this.showUploadPage(false)}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </TabPane>
          <TabPane tab={tabLabel(<Radio size={15} />, t.tabStreaming)} key="3">
            <div style={{ padding: isMobile ? "12px 0 8px" : "16px 12px 8px" }}>
              {sectionHeader(t.createStreamingUrl)}
              <CreateStreamingURLMediaView
                branches={childBranches}
                allBranchesOption={allBranchesOption}
                initialAccessBranches={initialAccessBranches}
                type="streaming"
                onFinish={() => this.showUploadPage(false)}
              />
            </div>
          </TabPane>
          <TabPane
            tab={tabLabel(<ExternalLink size={15} />, t.tabVideoUrl)}
            key="4"
          >
            <div style={{ padding: isMobile ? "12px 0 8px" : "16px 12px 8px" }}>
              {sectionHeader(t.createVideoUrl)}
              <CreateURLMediaView
                branches={childBranches}
                allBranchesOption={allBranchesOption}
                initialAccessBranches={initialAccessBranches}
                type="videourl"
                onFinish={() => this.showUploadPage(false)}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>
    );
  };
}

const mapStateToProps = (state) => {
  return {
    userdata: state.userdata,
    usertoken: state.usertoken,
    globalSearch: state.globalSearch,
    selectedBrandData: state.selectedBrandData,
    selectedBranchData: state.selectedBranchData,
  };
};

const ConnectedFilesUploader = connect(mapStateToProps, {
  requestServerGet,
  requestFilesServerPost,
  requestServerPost,
  selectBrand,
  selectPage,
})(FilesUploader);

const FilesUploaderWithLang = (props) => {
  const { lang } = useProLanguage();
  return <ConnectedFilesUploader {...props} lang={lang} />;
};

export default FilesUploaderWithLang;
